import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, LogOut, Copy, Download, LayoutDashboard, Users, DollarSign, LinkIcon, QrCode, Heart, Settings, Mail,
} from 'lucide-react';
import { ambAxios, clearAmbToken, getAmbToken, C, fmtMoney, fmtDate, apiError } from './ambApi';
import { AmbFooter } from './AmbassadorResources';

export default function AmbassadorDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');
  const [refs, setRefs] = useState([]);
  const [comms, setComms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getAmbToken()) { navigate('/ambassador/login'); return; }
    ambAxios().get('/ambassador/dashboard')
      .then((r) => setData(r.data))
      .catch((e) => { if (e?.response?.status === 401) { clearAmbToken(); navigate('/ambassador/login'); } else toast.error(apiError(e)); })
      .finally(() => setLoading(false));
    ambAxios().get('/ambassador/referrals').then((r) => setRefs(r.data.referrals)).catch(() => {});
    ambAxios().get('/ambassador/commissions').then((r) => setComms(r.data.commissions)).catch(() => {});
    ambAxios().get('/ambassador/available').then((r) => setWd(r.data)).catch(() => {});
    ambAxios().get('/ambassador/notifications').then((r) => setNotes(r.data.notifications.filter((n) => !n.read))).catch(() => {});
  }, [navigate]);

  const [wd, setWd] = useState(null);
  const [notes, setNotes] = useState([]);
  const dismissNotes = async () => { setNotes([]); try { await ambAxios().post('/ambassador/notifications/read'); } catch { /* ignore */ } };
  const [requesting, setRequesting] = useState(false);
  const requestPayout = async () => {
    setRequesting(true);
    try {
      const r = await ambAxios().post('/ambassador/payout-request');
      toast.success(`Payout of ${fmtMoney(r.data.amount)} requested — pending admin approval`);
      const av = await ambAxios().get('/ambassador/available'); setWd(av.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setRequesting(false); }
  };

  const logout = async () => { try { await ambAxios().post('/ambassador/logout'); } catch { /* */ } clearAmbToken(); navigate('/ambassador/login'); };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copied!'); };
  const [emailingSummary, setEmailingSummary] = useState(false);
  const emailSummary = async () => {
    setEmailingSummary(true);
    try { const r = await ambAxios().post('/ambassador/email-summary'); toast.success(`Summary emailed to ${r.data.sent_to}`); }
    catch (e) { toast.error(apiError(e)); }
    finally { setEmailingSummary(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA]"><Loader2 className="h-8 w-8 animate-spin" style={{ color: C.navy }} /></div>;
  if (!data) return null;
  const a = data.ambassador; const cards = data.cards;
  const name = `${a.first_name} ${a.last_name}`.trim();

  return (
    <div className="min-h-screen bg-[#F7F8FA] overflow-x-hidden" data-testid="ambassador-dashboard">
      <header className="bg-white border-b border-[#E8EAEE]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {a.profile_picture_url
              ? <img src={a.profile_picture_url} alt={name} className="w-12 h-12 rounded-full object-cover shrink-0" />
              : <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold shrink-0" style={{ background: C.navy2 }}>{(a.first_name || '?')[0]}</div>}
            <div className="min-w-0">
              <p className="font-bold truncate" style={{ color: C.ink }}>{name}</p>
              <p className="text-xs" style={{ color: C.mute }}>Code <b>{a.referral_code}</b> · <span className="capitalize">{a.status}</span> · {a.total_paid_signups} paid · {fmtMoney(a.total_commission_earned)} earned</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 shrink-0">
            <button onClick={emailSummary} disabled={emailingSummary} className="flex items-center gap-1.5 text-sm font-medium disabled:opacity-60" style={{ color: C.navy }} data-testid="amb-email-summary"><Mail className="h-4 w-4" /> Email my summary</button>
            <button onClick={() => navigate('/ambassador/resources')} className="flex items-center gap-1.5 text-sm font-medium" style={{ color: C.navy }} data-testid="amb-nav-resources"><Download className="h-4 w-4" /> Resources</button>
            <button onClick={() => navigate('/ambassador/settings')} className="flex items-center gap-1.5 text-sm font-medium" style={{ color: C.navy }} data-testid="amb-nav-settings"><Settings className="h-4 w-4" /> Settings</button>
            <button onClick={logout} className="flex items-center gap-1.5 text-sm font-medium" style={{ color: C.mute }} data-testid="amb-logout"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Referral tools */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="md:col-span-2 min-w-0 bg-white rounded-2xl border border-[#E8EAEE] p-5">
            <p className="font-semibold mb-3" style={{ color: C.ink }}>Your referral tools</p>
            <Tool icon={LinkIcon} label="Referral link" value={a.referral_link} onCopy={() => copy(a.referral_link)} />
            <Tool icon={QrCode} label="Referral code" value={a.referral_code} onCopy={() => copy(a.referral_code)} />
          </div>
          <div className="min-w-0 bg-white rounded-2xl border border-[#E8EAEE] p-5 text-center">
            <p className="font-semibold mb-3" style={{ color: C.ink }}>QR code</p>
            {a.qr_code_url ? (
              <>
                <img src={a.qr_code_url} alt="QR" className="w-32 h-32 mx-auto rounded-lg" data-testid="amb-qr" />
                <a href={a.qr_code_url} download={`qr-${a.referral_code}.png`} target="_blank" rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: C.navy2 }}><Download className="h-4 w-4" /> Download QR</a>
              </>
            ) : <p className="text-sm" style={{ color: C.mute }}>QR code will appear here.</p>}
          </div>
        </div>

        {/* Payout notifications */}
        {notes.length > 0 && (
          <div className="rounded-2xl p-4 mb-6 flex items-start justify-between gap-4" style={{ background: '#EAF3EE', border: '1px solid #CBE4D6' }} data-testid="amb-notifications">
            <div>
              {notes.map((n) => (
                <div key={n.notification_id} className="mb-1 last:mb-0">
                  <p className="font-semibold text-sm" style={{ color: '#2E7D5B' }}>{n.title}</p>
                  <p className="text-sm" style={{ color: '#3F6B54' }}>{n.body}</p>
                </div>
              ))}
            </div>
            <button onClick={dismissNotes} className="text-xs font-semibold shrink-0" style={{ color: '#2E7D5B' }} data-testid="amb-notifications-dismiss">Dismiss</button>
          </div>
        )}

        {/* Available to withdraw */}
        {wd && (
          <div className="bg-white rounded-2xl border border-[#E8EAEE] p-5 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="amb-withdraw-card">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide" style={{ color: C.mute }}>Available to withdraw</p>
              <p className="text-3xl font-bold mt-1" style={{ color: C.green }} data-testid="amb-available-amount">{fmtMoney(wd.available)}</p>
              {wd.pending_request
                ? <p className="text-xs mt-1" style={{ color: C.gold }}>Payout of {fmtMoney(wd.pending_request.amount)} pending admin approval</p>
                : !wd.payouts_enabled
                  ? <p className="text-xs mt-1" style={{ color: C.mute }}>Connect your Stripe payout account in Settings to withdraw.</p>
                  : <p className="text-xs mt-1" style={{ color: C.mute }}>Minimum payout {fmtMoney(wd.min_payout)}.</p>}
            </div>
            {wd.payouts_enabled
              ? <button onClick={requestPayout} disabled={!wd.can_request || requesting}
                  className="h-11 px-6 rounded-full text-white font-semibold disabled:opacity-50 shrink-0" style={{ background: C.navy }} data-testid="amb-request-payout">
                  {requesting ? 'Requesting…' : wd.pending_request ? 'Request pending' : 'Request payout'}
                </button>
              : <button onClick={() => navigate('/ambassador/settings')} className="h-11 px-6 rounded-full font-semibold border shrink-0" style={{ borderColor: C.navy, color: C.navy }} data-testid="amb-setup-payouts-cta">Set up payouts</button>}
          </div>
        )}

        {/* Tabs */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[['overview', 'Dashboard', LayoutDashboard], ['referrals', 'Referrals', Users], ['commissions', 'Commissions', DollarSign]].map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`amb-tab-${id}`}
              className={`flex items-center justify-center gap-1.5 px-2 h-10 rounded-full text-xs sm:text-sm font-medium ${tab === id ? 'text-white' : 'bg-white border border-[#E8EAEE]'}`}
              style={tab === id ? { background: C.navy } : { color: C.ink }}><Icon className="h-4 w-4 shrink-0" /> <span className="truncate">{label}</span></button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="amb-cards">
            {[['Total Clicks', cards.total_clicks, C.navy], ['Free Signups', cards.free_signups, C.mute],
              ['Paid Subscribers', cards.paid_subscribers, C.green], ['Active Paid', cards.active_paid, C.green],
              ['Cancelled', cards.cancelled, C.red], ['Pending', fmtMoney(cards.pending_commissions), C.gold],
              ['Approved', fmtMoney(cards.approved_commissions), C.navy2], ['Paid Out', fmtMoney(cards.paid_commissions), C.green]].map(([l, v, c]) => (
              <div key={l} className="bg-white rounded-2xl border border-[#E8EAEE] p-5">
                <p className="text-xs font-medium uppercase tracking-wide" style={{ color: C.mute }}>{l}</p>
                <p className="text-2xl font-bold mt-1.5" style={{ color: c }}>{v}</p>
              </div>
            ))}
            <div className="bg-white rounded-2xl border border-[#E8EAEE] p-5 col-span-2">
              <p className="text-xs font-medium uppercase tracking-wide" style={{ color: C.mute }}>Next payout date</p>
              <p className="text-lg font-bold mt-1.5" style={{ color: C.ink }}>{cards.next_payout_date ? fmtDate(cards.next_payout_date) : 'No pending payout'}</p>
            </div>
          </div>
        )}

        {tab === 'referrals' && (
          <Table cols={['Date', 'Customer', 'Signup', 'Plan', 'Payment', 'Commission', 'Status']}
            rows={refs.map((r) => [fmtDate(r.date), r.customer, r.signup_status, r.plan, r.payment_status, fmtMoney(r.commission_earned), r.commission_status])}
            empty="No referrals yet — share your link to get started!" testid="amb-referrals-table" />
        )}
        {tab === 'commissions' && (
          <Table cols={['Earned', 'Type', 'Payment', 'Amount', 'Status', 'Payable']}
            rows={comms.map((c) => [fmtDate(c.earned_date), c.commission_type.replace(/_/g, ' '), fmtMoney(c.payment_amount), fmtMoney(c.commission_amount), c.commission_status, fmtDate(c.payable_date)])}
            empty="No commissions yet." testid="amb-commissions-table" />
        )}
      </div>
      <AmbFooter />
    </div>
  );
}

const Tool = ({ icon: Icon, label, value, onCopy }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-[#F1F2F4] last:border-0">
    <Icon className="h-4 w-4 shrink-0" style={{ color: C.navy2 }} />
    <div className="flex-1 min-w-0"><p className="text-xs" style={{ color: C.mute }}>{label}</p><p className="text-sm font-medium truncate" style={{ color: C.ink }}>{value}</p></div>
    <button onClick={onCopy} className="text-xs px-3 py-1.5 rounded-full border flex items-center gap-1" style={{ borderColor: C.line, color: C.navy }}><Copy className="h-3.5 w-3.5" /> Copy</button>
  </div>
);

const Table = ({ cols, rows, empty, testid }) => (
  <div className="bg-white rounded-2xl border border-[#E8EAEE] overflow-x-auto" data-testid={testid}>
    {rows.length === 0 ? <div className="text-center py-16 text-sm" style={{ color: C.mute }}>{empty}</div> : (
      <table className="w-full text-sm">
        <thead><tr className="text-left border-b border-[#E8EAEE]" style={{ color: C.mute }}>{cols.map((c) => <th key={c} className="px-4 py-3 font-medium whitespace-nowrap">{c}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-[#F1F2F4]">{r.map((cell, j) => <td key={j} className="px-4 py-3 capitalize whitespace-nowrap" style={{ color: C.ink }}>{cell}</td>)}</tr>)}</tbody>
      </table>
    )}
  </div>
);
